import { SyntheticEvent, useCallback, useState } from "react";

import { twMerge } from "tailwind-merge";

import { faAngleDoubleLeft } from "@fortawesome/free-solid-svg-icons/faAngleDoubleLeft";
import { faAngleDoubleRight } from "@fortawesome/free-solid-svg-icons/faAngleDoubleRight";
import { faSpinner } from "@fortawesome/free-solid-svg-icons/faSpinner";

import Checkbox from "components/Checkbox";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import { useApi } from "hooks/useApi";
import { baseFetcher } from "utils/baseFetcher";

export const geographiesUrl = "/api/v2/geographies?include=countries";
export const countriesUrl = "/api/v2/countries?sort=name";

type RegionsFilterProps = {
  overrideEndpoints?: { geographies?: string; countries?: string };
  selected?: { geographies?: string[]; countries: string[] };
  onGeographiesChange?: (_geographies: string[]) => void;
  onCountriesChange: (_countries: any[]) => void;
  showTopCountries?: boolean;
  dropdownClassName?: string;
};

const RegionsFilter = ({
  overrideEndpoints,
  selected,
  onGeographiesChange,
  onCountriesChange,
  showTopCountries,
  dropdownClassName,
}: RegionsFilterProps) => {
  const { data: allGeographies, isValidating: isRegionsValidating } = useApi(
    overrideEndpoints?.geographies || geographiesUrl,
    baseFetcher
  );

  const { data: allCountries, isValidating: isCountriesValidating } = useApi(
    overrideEndpoints?.countries || countriesUrl,
    baseFetcher
  );

  const countries = allCountries?.filter?.(
    (country: any) => country.totalInsights > 0
  );

  const topCountries = countries
    ?.sort((c1: any, c2: any) => (c1.totalInsights > c2.totalInsights ? -1 : 1))
    .slice(0, 10);

  const geographies = allGeographies?.filter?.((geography: any) =>
    geography.countries.some((c: any) =>
      countries?.some((cc: any) => cc.slug === c.slug)
    )
  );

  const [showAllCountries, setShowAllCountries] = useState(false);

  const onGeographyChange = useCallback(
    (e: SyntheticEvent<HTMLInputElement, Event>, geography: any) => {
      let geographies = selected?.geographies || [];

      if (e.currentTarget.checked) {
        if (!geographies?.some((g: any) => g.name === geography.name))
          geographies?.push(geography);
      } else {
        geographies = geographies?.filter?.(
          (g: any) => g.name !== geography.name
        );
      }
      onGeographiesChange?.(
        allGeographies.filter((g: any) =>
          geographies?.some((cg: any) => g.name === cg.name)
        )
      );
    },
    [allGeographies, onGeographiesChange, selected?.geographies]
  );

  const onCountryChange = useCallback(
    (e: SyntheticEvent<HTMLInputElement, Event>, country: any) => {
      let countries = selected?.countries || [];

      if (e.currentTarget.checked) {
        if (!countries?.some((g: any) => g.name === country.name))
          countries?.push(country);
      } else {
        countries = countries?.filter?.((g: any) => g.name !== country.name);
      }
      onCountriesChange(
        allCountries.filter((g: any) =>
          countries?.some((cg: any) => g.name === cg.name)
        )
      );
    },
    [allCountries, onCountriesChange, selected?.countries]
  );

  return (
    <div className="flex flex-col md:flex-row md:space-x-[10px] space-y-[30px] md:space-y-0">
      {(isRegionsValidating || isCountriesValidating) &&
      (!geographies?.length || !countries?.length) ? (
        <div>
          <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> Loading
        </div>
      ) : (
        <>
          {onGeographiesChange && (
            <div className="min-w-[230px]">
              <div className="font-bold pb-[10px]">Regions</div>
              <div
                className={twMerge(
                  "overflow-y-auto space-y-[20px]",
                  dropdownClassName
                )}
              >
                {geographies?.map?.((geography: any) => (
                  <Checkbox
                    key={geography.name}
                    className="block"
                    checked={selected?.geographies?.some(
                      (g: any) => g.name === geography.name
                    )}
                    onChange={(e) => onGeographyChange(e, geography)}
                  >
                    {geography.name}
                  </Checkbox>
                ))}
              </div>
              {geographies?.length === selected?.geographies?.length ? (
                <div
                  className="cursor-pointer pt-[15px] pl-[15px] text-smart-teal"
                  onClick={() => onGeographiesChange([])}
                >
                  Deselect All
                </div>
              ) : (
                <div
                  className="cursor-pointer pt-[15px] pl-[15px] text-smart-teal"
                  onClick={() => onGeographiesChange(geographies)}
                >
                  Select All
                </div>
              )}
            </div>
          )}
          {showTopCountries && (
            <div className="min-w-[230px]">
              <div className="font-bold pb-[10px]">Top Countries</div>
              <div
                className={twMerge(
                  "overflow-y-auto space-y-[20px]",
                  dropdownClassName
                )}
              >
                {topCountries?.map?.((country: any) => (
                  <Checkbox
                    key={country.name}
                    className="block"
                    checked={selected?.countries?.some(
                      (c: any) => c.name === country.name
                    )}
                    onChange={(e) => onCountryChange(e, country)}
                  >
                    {country.name}
                  </Checkbox>
                ))}
              </div>
              {!topCountries?.some(
                (c1: any) =>
                  !selected?.countries.some((c2: any) => c1.name === c2.name)
              ) ? (
                <div
                  className="cursor-pointer pt-[15px] pl-[15px] text-smart-teal"
                  onClick={() =>
                    onCountriesChange(
                      selected?.countries?.filter?.(
                        (c1: any) =>
                          !topCountries?.some((c2: any) => c1.name === c2.name)
                      ) || []
                    )
                  }
                >
                  Deselect All
                </div>
              ) : (
                <div
                  className="cursor-pointer pt-[15px] pl-[15px] text-smart-teal"
                  onClick={() => onCountriesChange(topCountries)}
                >
                  Select All
                </div>
              )}
              {countries?.length > 10 && (
                <div className="mt-[20px]">
                  <div
                    className="cursor-pointer text-smart-teal"
                    onClick={() => setShowAllCountries(!showAllCountries)}
                  >
                    {showAllCountries ? "Hide" : "Show"} All Countries{" "}
                    <FontAwesomeIcon
                      icon={
                        showAllCountries
                          ? faAngleDoubleLeft
                          : faAngleDoubleRight
                      }
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          {(showAllCountries || !showTopCountries) && (
            <div className="min-w-[230px]">
              <div className="font-bold pb-[10px]">
                {showTopCountries ? "All Countries" : "Countries"}
              </div>
              <div
                className={twMerge(
                  "max-h-[430px] overflow-y-auto space-y-[20px]",
                  dropdownClassName
                )}
              >
                {allCountries?.map?.((country: any, index: number) => (
                  <>
                    {allCountries.length >= 50 &&
                      (index === 0 ||
                        country.name[0] !==
                          allCountries[index - 1].name[0]) && (
                        <div className="uppercase">{country.name[0]}</div>
                      )}
                    <Checkbox
                      key={country.name}
                      className="block"
                      checked={selected?.countries?.some(
                        (c: any) => c.name === country.name
                      )}
                      onChange={(e) => onCountryChange(e, country)}
                    >
                      {country.name}
                    </Checkbox>
                  </>
                ))}
              </div>
              {countries?.length === selected?.countries?.length ? (
                <div
                  className="cursor-pointer pt-[15px] pl-[15px] text-smart-teal"
                  onClick={() => onCountriesChange([])}
                >
                  Deselect All
                </div>
              ) : (
                <div
                  className="cursor-pointer pt-[15px] pl-[15px] text-smart-teal"
                  onClick={() => onCountriesChange(countries)}
                >
                  Select All
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RegionsFilter;
